import {
  type RemovableRef,
  useLocalStorage,
  useSessionStorage,
} from '@vueuse/core';
import { defineStore } from 'pinia';
import { Logger } from '@/common/Logger';
import { getItemRating } from '@/common/api/getItemRating';
import { getItems } from '@/common/api/getItems';
import type { SortByOption } from '@/common/constants/items';
import { OK } from '@/common/constants/status';
import { formatDate } from '@/common/dateUtils';
import { handleThrownError } from '@/common/errors/handleThrownError';
import { useCurrenciesStore } from '@/stores/currencies';
import { useItemFiltersStore } from '@/stores/itemFilters';
import { useSearchStore } from '@/stores/search';
import { useTagsStore } from '@/stores/tags';
import {
  type Item,
  type ItemPayload,
  ItemRatingProvider,
  type PagedItemsMap,
} from '@/types';

interface ItemsStoreState {
  pagedItems: PagedItemsMap;
  nextPageUrl: string | null;
  totalCount: RemovableRef<number>;
  isLoading: boolean;
  filtersEnabled: RemovableRef<boolean>;
}

export function transformSingleItemData(itemPayload: ItemPayload): Item {
  try {
    if (!itemPayload) {
      throw new Error('Invalid item payload received.');
    }
    const referralLinks =
      itemPayload.referral_links !== null
        ? {
            regularLink: itemPayload.referral_links.regular_link,
            calendarLink: itemPayload.referral_links.calendar_link,
            calendarScript: itemPayload.referral_links.calendar_script,
          }
        : null;
    const item = {
      itemId: itemPayload.id,
      name: itemPayload.name,
      shortName: itemPayload.short_name,
      headline: itemPayload.headline,
      location: itemPayload.location,
      companyId: itemPayload.company_id,
      tags: itemPayload.tags,
      company: {
        id: itemPayload.company?.id,
        companyUrl: itemPayload.company?.company_url,
        companyImage: itemPayload.company?.image_logo_url,
        name: itemPayload.company?.name,
        shortName: itemPayload.company?.shortname,
        language: itemPayload.company?.language,
        timezone: itemPayload.company?.timezone,
        about: itemPayload.company?.about,
        imageUrl: itemPayload.company?.image_url,
        createdAt: formatDate(itemPayload.company?.created_at),
        modifiedAt: formatDate(itemPayload.company?.modified_at),
        primaryLocation: itemPayload.company?.primary_location,
        currency: itemPayload.company?.currency,
      },
      mainImageUrl: itemPayload.main_image_url,
      createdAt: formatDate(itemPayload.created_at),
      modifiedAt: formatDate(itemPayload.modified_at),
      extraImageCount: Math.max(itemPayload.image_count - 1, 0),
      qualityScore: itemPayload.quality_score,
      companyItemCount: itemPayload.company_item_count,
      isBookmarked: itemPayload.is_bookmarked,
      description: itemPayload.description,
      selectedLocation: itemPayload.selected_location
        ? {
            id: itemPayload.selected_location.id,
            city: itemPayload.selected_location.city,
            state: itemPayload.selected_location.state,
            country: itemPayload.selected_location.country,
            coordinates: {
              x: itemPayload.selected_location?.coordinates?.x,
              y: itemPayload.selected_location?.coordinates?.y,
            },
          }
        : null,
      ...(itemPayload.structured_description && {
        structuredDescription: {
          id: itemPayload.structured_description?.id,
          duration: itemPayload.structured_description?.duration,
          cancellationSummary:
            itemPayload.structured_description?.cancellation_summary,
          meetingPoint: itemPayload.structured_description?.meeting_point,
          minAge: itemPayload.structured_description?.min_age,
          maxAge: itemPayload.structured_description?.max_age,
          groupSize: itemPayload.structured_description?.group_size,
          accessibility: itemPayload.structured_description?.accessibility,
          pricing: itemPayload.structured_description?.pricing,
          description: itemPayload.structured_description?.description,
          whatIsIncluded: itemPayload.structured_description?.what_is_included,
          whatIsNotIncluded:
            itemPayload.structured_description?.what_is_not_included,
          itinerary: itemPayload.structured_description?.itinerary,
          highlights: itemPayload.structured_description?.highlights,
          checkInDetails: itemPayload.structured_description?.check_in_details,
          whatToBring: itemPayload.structured_description?.what_to_bring,
          specialRequirements:
            itemPayload.structured_description?.special_requirements,
          restrictions: itemPayload.structured_description?.restrictions,
          extras: itemPayload.structured_description?.extras,
          disclaimers: itemPayload.structured_description?.disclaimers,
          faqs: itemPayload.structured_description?.faqs,
        },
      }),
      referralLinks,
      rating: null,
      availabilityCount: itemPayload.availability_count,
    };
    Logger.debug(`Transformed item:' ${item}`);
    return item;
  } catch (error) {
    handleThrownError({
      error,
      toastMessage: 'Unexpected error occurred.',
      log: true,
    });
    throw error;
  }
}

export async function supplementSingleItemWithRating(
  item: Item,
): Promise<Item> {
  try {
    const { status, error, data } = await getItemRating(
      item.company.shortName,
      item.itemId,
    );

    // If the request was successful and we have data
    if (!error && data && status === OK) {
      if (data.provider === 'google_reviews') {
        return {
          ...item,
          rating: {
            provider: ItemRatingProvider.GOOGLE_REVIEWS,
            rating: data.rating,
            reviewCount: data.review_count,
          },
        };
      }
      if (data.provider === 'tripadvisor') {
        return {
          ...item,
          rating: {
            provider: ItemRatingProvider.TRIP_ADVISOR,
            rating: data.rating,
            reviewCount: data.review_count,
          },
        };
      }
    }

    // If there's an error but it's not critical (network issues, CORS, etc.)
    // Just log it and return the item without rating data
    if (error || status === 0) {
      Logger.debug(
        `Rating not available for item ${item.itemId} (${item.company.shortName}): ${error?.message || 'Network or CORS issue'}`,
      );
    }

    return item;
  } catch (error) {
    // This should rarely happen since getItemRating handles its own errors
    Logger.debug(
      `Unexpected error loading rating for item ${item.itemId}: ${error}`,
    );
    return item;
  }
}

interface LoadPageItemsOptions {
  page?: number | null;
  pageSize: number | null;
  sortBy?: SortByOption | null;
}

export function transformItemsData(payload: any): Item[] {
  if (!payload || !Array.isArray(payload.results)) {
    throw new Error('Invalid data structure received from API.');
  }

  return payload.results.map((item: any) => transformSingleItemData(item));
}

export const useItemsStore = defineStore('itemsStore', {
  state: (): ItemsStoreState => ({
    pagedItems: new Map<number, Array<Item>>(),
    nextPageUrl: null,
    totalCount: useLocalStorage('totalCount', 0),
    filtersEnabled: useSessionStorage('inventory/items/filters/enabled', true),
    isLoading: true,
  }),

  getters: {
    nextPageNumber(): number | null {
      if (this.nextPageUrl === null) return null;

      try {
        const url = new URL(this.nextPageUrl);
        const page = url.searchParams.get('page');
        if (page === null) return null;
        return parseInt(page, 10);
      } catch (error) {
        handleThrownError({
          error,
          toastMessage: 'Failed to parse next page number.',
          log: true,
        });
        return null;
      }
    },

    itemsByPage(): (page: number) => Array<Item> {
      return (page: number) => [...(this.pagedItems.get(page) ?? [])];
    },
  },

  actions: {
    async loadPageItems({
      page = 1,
      pageSize = 10,
      sortBy,
    }: LoadPageItemsOptions) {
      Logger.debug(`Loading ${pageSize} items for endpoint page: ${page}`);

      const itemFiltersStore = useItemFiltersStore();
      const searchStore = useSearchStore();
      const currenciesStore = useCurrenciesStore();
      const tagsStore = useTagsStore();
      const options = {
        sortBy,
        tags: null,
        offeredSince: itemFiltersStore.offeredSince,
        currencies: itemFiltersStore.currencies,
        searchLocation: searchStore.locationSearch,
        search: searchStore.companyItemSearch,
        isBookmarked: itemFiltersStore.isBookmarked,
      };

      // Get coordinate parameters if available
      const coordinates = searchStore.locationCoordinates;
      if (coordinates) {
        Object.assign(options, coordinates);
      }

      currenciesStore.loadAllCurrencies(options);
      tagsStore.loadAllTags(options);

      const fullOptions = {
        ...options,
        page,
        pageSize,
        tags: itemFiltersStore.tags,
      };

      try {
        this.isLoading = true;

        const { status, data } = await getItems(fullOptions);

        if (status !== 200 || !data) {
          throw new Error('Failed to load Items');
        }

        const currentPage = page ?? 1;
        const transformedItems = transformItemsData(data);

        // Set initial items
        this.pagedItems.set(currentPage, transformedItems);
        this.nextPageUrl = data.next;
        this.totalCount = data.count;

        // Mark loading as complete so table can be displayed immediately
        this.isLoading = false;

        // Load supplementary data asynchronously
        this.loadSupplementaryData(currentPage, transformedItems);
      } catch (error) {
        handleThrownError({
          error,
          toastMessage: 'Something went wrong while loading items.',
          log: true,
        });
        this.isLoading = false;
      }
    },

    async loadSupplementaryData(currentPage: number, items: Item[]) {
      try {
        // Process each item's supplementary data with staggered delays to prevent overwhelming the backend
        items.forEach(async (item, index) => {
          try {
            // Add a small staggered delay to prevent DDos-like behavior
            // Each request is delayed by index * 100ms (0ms, 100ms, 200ms, etc.)
            await new Promise<void>((resolve) => {
              setTimeout(() => resolve(), index * 100);
            });

            // Load rating data for this item
            const withRating = await supplementSingleItemWithRating(item);

            // Create updated item with supplementary data
            const updatedItem = {
              ...withRating,
            };

            // Get current items for this page
            const currentItems = this.pagedItems.get(currentPage) || [];
            if (currentItems.length > index) {
              // Replace the item at its index with the updated version
              currentItems[index] = updatedItem;
              // Update the page with the modified items array
              this.pagedItems.set(currentPage, [...currentItems]);
            }
          } catch (itemError) {
            // Log error but don't fail the entire process
            Logger.error(
              `Failed to load supplementary data for item ${item.itemId}: ${itemError}`,
            );
          }
        });
      } catch (error) {
        Logger.error(`Error loading supplementary data: ${error}`);
      }
    },

    clear() {
      this.pagedItems.clear();
    },

    hideFilters() {
      this.filtersEnabled = false;
    },

    showFilters() {
      this.filtersEnabled = true;
    },
  },
});
